import { clone, debounce, isUndefined } from 'lodash';
import { createLogger } from 'shared/services/logger';
import { makeDraggable } from 'uScope/js/components/mixins/draggable-mixin';
import { addPointerListener } from 'uScope/js/lib/utility/pointerApi';
import us from '../../defines';
import { Point, Size } from '../../lib/objects/';
import { divRotate } from '../../lib/utility/helpers';

const logger = createLogger('UScope:extensions:MagnifierControl');

//    This implementation of magnification does not update the magnified image while
//    drag or resize is happening. Instead it removes the magnified image and permits
//    the viewport image to be visible within the outline of the magnifier.
//    This is because the current magnification implementation goes to the server for
//    each image update.  A better way would be to get a larger region or to build
//    a view like viewport does.
//
//    improvements:
//        - I'm not certain magSuspend is nec anymore.  Mode changes can now show/hide overlays.

const defaults = {
  layout: {'divClass': us.clsDIVMAG, 'precinct': 'BR', 'order': 1, 'canBePlaced': true}
};

export default function MagnifierControl (uScope, viewport, userConfig) {
  const self = this;
  let isLoaded = false; // magnifier has been loaded
  const namespace = viewport.eventNameSpaceGet();

  const jVpDiv = viewport.vpDivGet('outer');
  let jMagCtlDiv;
  let magSuspended = true; // magnifier suspension; start it suspended & in modes like autopanning, suspend it

  const magSpecs = { // magnifier settings
    'minDiameter': 64,
    'maxDiameter': 400, // resize limits in pixels
    'initialDiameter': 150, // initial size in pixels
    'minFactor': 1,
    'maxFactor': 4, // magnification limits in percent
    'magFactor': 2 // current magnification factor
  };
  const xHairFullDiam_ = false; // do full diameter crosshairs
  const xHairAlwaysShow_ = false; // always show the crosshairs

  const navModesImHiddenIn = {}; // what navigation modes not to display the label

  const layout = clone(defaults.layout);
  const reLayout = viewport.overLayout.refreshPrecinct;

  // ............................................ construct_ local
  const construct_ = function () {
    navModesImHiddenIn[us.eNavModes.nmRasterScan] = true; // hide magnfier in rasterscan mode
    navModesImHiddenIn[us.eNavModes.nmAutoPan] = true; // hide magnfier in when auto panning
    us.setAnnotNavModes(navModesImHiddenIn); // hide magnfier when drawing any annotation

    load_(); // load the magnfier

    // allow the magnifier updates but don't refresh on self call
    self.resume();
  };

  self.updateVisibility = function () {
    const isVisible =
            userConfig.get(us.clsDIVMAG) &&
            viewport.getImageIdx() === uScope.getSelImageIdx() &&
            !navModesImHiddenIn[viewport.navModeGet()];
    show(isVisible);
  };

  const show = function (show) {
    if (!isLoaded) {
      return;
    }

    if (show) {
      jMagCtlDiv.show();
      self.refresh();
    } else {
      jMagCtlDiv.hide();
    }

    reLayout(layout.precinct); // layout just this overlay's precinct
  };

    // ............................................ refresh() public
    //    This function updates the magnifier's view if it is visible.
    //    Notes:
    //        Unlike the viewport, the magnifier is not concerned about requesting tiles, it requests the region of interest.
    //        If the view is at 20x and the magFactor is 2 then the magnifier displays a 40x image
    //        Note that ImageServer's GETImage likes zoom : 0.5 = 200%, 1 = 100%, 2 = 50%, 4 = 25% (counter intuitive)
  this.refresh = debounce(function () {
    if (jMagCtlDiv == undefined) return;
    if (jMagCtlDiv.is(':visible') == false) return; // if the magnifier is not visible, return

    // in modes like autopanning, the magnifier update gets in the way
    // so these modes suspend the magnifier. If it is suspended, return now.
    if (magSuspended) return;

    const magZoom = Number((viewport.getViewZoom() / magSpecs.magFactor).toFixed(2)); // magnifier zoom
    const csMag = new Size(jMagCtlDiv.width(), jMagCtlDiv.height()); // dimensions of the magnifier in pixels

    const e = jQuery.Event('', { // create an event at center  of the mag
      pageX: parseInt(jMagCtlDiv.offset().left + csMag.w / 2, 10),
      pageY: parseInt(jMagCtlDiv.offset().top + csMag.h / 2, 10)
    });

    const cpMagCenter = us.coordBaseImage(e, viewport); // compute the position of the mag center in base image coords
    const cpMagLT = new Point(cpMagCenter.x - csMag.w / 2 * magZoom, // then back up to the mag left-top corner
      cpMagCenter.y - csMag.h / 2 * magZoom);
    cpMagLT.toInt();

    cpMagLT.x = '0' + cpMagLT.x; // append a leading 0 to the requested coordinates
    cpMagLT.y = '0' + cpMagLT.y; // so they are interpreted by ImageServer as relative to the image base coords
    // build the url
    const sMagImgSrc = viewport.urlBuilder.region(cpMagLT.x, cpMagLT.y, csMag.w, csMag.h, magZoom);

    const jRegion = regionBuildDiv(sMagImgSrc, viewport);
    jRegion.attr('class', us.clsIMGMAG); // change the class name from us.clsIMGREGION to us.clsIMGMAG

    const jMagWrapDiv = jMagCtlDiv.find('.' + us.clsDIVIMGWRAP); // display the magnified region
    const jMatches = jMagWrapDiv.find('.' + us.clsIMGMAG);
    if (jMatches.length) { // if the mag currently has an image
      jMatches.replaceWith(jRegion); // replace it
    } else {
      jMagWrapDiv.append(jRegion); // add the new region to the magnifier
    }

    const angleRad = viewport.getViewAngle();
    if (angleRad !== 0.0) {
      divRotate(jRegion[0], 0, 0, jRegion.width(), jRegion.height(), angleRad);
    }
  }, us.kREFRESH_RATE);

  // ............................................ suspend() public
  //    This function disables magnifier image updates.
  this.suspend = function () {
    magSuspended = true;
  };
  // ............................................ resume() public
  //    This function enables magnifier image updates
  //    and refreshes the magnifier if the passed parm 'doRefresh' is true.
  this.resume = function (doRefresh) {
    magSuspended = false;
    if (doRefresh) { self.refresh(); }
  };

  // ............................................ load_() local
  //    This function builds the magnfier control and calls into the layout manager
  //        to position it according to its layout settings.
  const load_ = function () {
    if (jMagCtlDiv) return; // if this viewport already has a magnifier, return

    const sDiam = magSpecs.initialDiameter + 'px';
    jMagCtlDiv = $('<div/>', { // build the div tag that contains the magnified images
      'class': us.clsDIVMAG + ' ' + us.clsSUPEROVERLAY,
      css: {
        'width': sDiam,
        'height': sDiam,

        '-moz-border-radius': sDiam,
        '-webkit-border-radius': sDiam,
        'border-radius': sDiam,
        '-khtml-border-radius': sDiam
      }
    });
    // add an inner div before the magnified image
    const jMagWrapDiv = $('<div/>', {'class': us.clsDIVIMGWRAP}); // necessary for webkit browsers for round mag and the resizer knob
    jMagCtlDiv.append(jMagWrapDiv);

    jVpDiv.append(jMagCtlDiv); // place the magnifier div in the viewport
    jMagCtlDiv = jVpDiv.find('.' + us.clsDIVMAG); // save a quick access reference to the magnifier div

    viewport.overLayout.add(layout); // and call into layout with parms to place the div

    isLoaded = true;
    self.updateVisibility(); // show/hide according to user preferences
    bindEvents_(); // add event handlers

    // ..................................................
    // closure functions
    // ............................................ bindEvents_()
    // bindEvents_ - This function adds the magnifier event handlers.
    //
    //    Note that by using the same namespace as the viewport when the
    //    viewport removes its events by namespace, these are also removed.

    function bindEvents_ () {
      uScope.events.on('uscope:viewport:selected', self.updateVisibility);
      userConfig.events.on('load', self.updateVisibility);
      userConfig.events.on(`change:${us.clsDIVMAG}`, self.updateVisibility);

      // TODO: Think about how magnifier might leverage new renderer
      viewport.renderer.addLayer({
        paint: self.refresh
      });
      viewport.events.on('viewport:resize', self.refresh);

      $(us.idHashROOT).on(us.evMODECHANGE + namespace, self.updateVisibility);

      jMagCtlDiv.resizable({
        cursor: 'auto',
        handles: 'se',
        containment: 'parent',
        aspectRatio: 1,
        distance: 3,
        minWidth: magSpecs.minDiameter,
        minHeight: magSpecs.minDiameter,
        maxWidth: magSpecs.maxDiameter,
        maxHeight: magSpecs.maxDiameter,
        resize: function (e) {
          clear_();
          resize_(true);
          e.preventDefault();
          return false;
        },
        stop: function (e, ui) {
          self.refresh();
          return false;
        }
      });

      jMagCtlDiv.resizable({handles: 'se'});
      const $handle = jMagCtlDiv.find('.ui-resizable-se');
      $handle.css({'left': '84%', 'top': '84%'});

      const magCtlDiv = jMagCtlDiv[0];
      makeDraggable(magCtlDiv, magCtlDiv, $handle[0]);

      addPointerListener(magCtlDiv, 'mousedown', function (e) {
        clear_();
        showCrosshairs_(true, false); // show mag crosshairs without resizing
        return false; // prevent propagation to VP
      });

      addPointerListener(magCtlDiv, 'mousemove', function (e) {
        return true; // propigate necessary or scalebar resize does not reduce in size
      });

      addPointerListener(magCtlDiv, 'mouseup', function (e) {
        layout.canBePlaced = false; // reposition only when magnifier is not fully within view
        showCrosshairs_(false);
        self.refresh();
        return true; // propagate necessary or scalebar resize does not release
      });
    }
  };

    // ............................................ resize_() local
    //    This function resizes the magnifier, sets its shape and its crosshairs.
    //    Optionally and by default, it refreshes its magnified image.
  function resize_ (bDoRefresh) {
    if (!jMagCtlDiv) {
      return;
    }

    const width = Math.max(magSpecs.minDiameter, jMagCtlDiv.width());
    const str = width + 'px';
    jMagCtlDiv.css({
      'width': str,
      'height': str,
      'border-radius': str
    });

    showCrosshairs_(true, true); // resize and show the crosshairs inside the magnifier
    if (isUndefined(bDoRefresh) || bDoRefresh) { // if instructed, refresh the magnifier image
      self.refresh();
    }
  }

  // ............................................ clear_() local
  //    This function removes the content from the magnifier.  It is used to show the
  //    underlying image when the magnifier is resizing or dragging.
  function clear_ () {
    if (!jMagCtlDiv) {
      return;
    }

    // if there is a region from a previous position in the mag, hide it
    const jMatches = jMagCtlDiv.find('.' + us.clsIMGMAG);
    if (jMatches.length) { jMatches.hide(); }
  }
  // ............................................ showCrosshairs_() local
  //    This function draws and shows/hides the crosshairs in the magnifier.
  function showCrosshairs_ (show, resizeX) {
    if (!jMagCtlDiv) {
      return;
    }
    // override show if always show is enabled
    if (xHairAlwaysShow_) {
      show = xHairAlwaysShow_;
    }

    const jMagXHairs = jMagCtlDiv.find('.' + us.clsMAGXHAIR); // find the cross hairs

    if (show && (resizeX || jMagXHairs.length === 0)) { // if showing and (instructed to resize or the xhairs don't yet exist)
      if (jMagXHairs.length > 0) { // if they exist, remove them
        jMagXHairs.remove();
      }

      const width = jMagCtlDiv.width(); // and rebuild them
      const classes = us.clsMAGXHAIR + ' ' + us.clsSUPEROVERLAY;
      let length;
      let offsetLT;
      let radius;

      if (!xHairFullDiam_) {
        length = Math.floor(width / 2 - width / 8) + 'px';
        offsetLT = Math.floor(width / 2 + width / 8) + 'px';
        radius = Math.floor(width / 2) + 'px';
      } else {
        length = Math.floor(width / 2) + 'px';
        offsetLT = Math.floor(width / 2) + 'px';
        radius = Math.floor(width / 2) + 'px';
      }

      let jXHair = $('<div/>', {
        'class': classes,
        css: {'left': '0px', 'top': radius, 'width': length, 'height': '2px'}
      });
      jMagCtlDiv.append(jXHair); // place the horiz left crosshairs in the magnifier
      jXHair = $('<div/>', {
        'class': classes,
        css: {'left': offsetLT, 'top': radius, 'width': length, 'height': '2px'}
      });
      jMagCtlDiv.append(jXHair); // place the horiz right crosshairs in the magnifier
      jXHair = $('<div/>', {
        'class': classes,
        css: {'left': radius, 'top': '0px', 'width': '2px', 'height': length}
      });
      jMagCtlDiv.append(jXHair); // place the vert top crosshairs in the magnifier
      jXHair = $('<div/>', {
        'class': classes,
        css: {'left': radius, 'top': offsetLT, 'width': '2px', 'height': length}
      });
      jMagCtlDiv.append(jXHair); // place the vert bottom crosshairs in the magnifier
    }

    if (show) jMagXHairs.show();
    else jMagXHairs.hide();
  }

  construct_();
}

function regionBuildDiv (sRegImgSrc, thisImg, opt_cbOnRegionLoad) {
  return $('<img/>', {
    load: function (e) {
      if (opt_cbOnRegionLoad) { opt_cbOnRegionLoad(self); }
    },
    error: function (e) {
      logger.error('..regionBuildDiv: load error', e);
    },

    class: us.clsIMGREGION,
    src: sRegImgSrc,
    alt: ''
  });
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/magnifier/magnifier-control.js